import { ProductCard } from '@dfh-corp-sites/ui'
import { Maybe } from 'graphql/jsutils/Maybe'
import React, { FC } from 'react'

import { ParentProduct, SanityImage } from '../../../graphql/search'
import useRetailPrices from '../../../hooks/useRetailPrices'
import { useTestKitPrice } from '../../../hooks/useTestKitPrice'
import { AnalyticsEventAddToCart } from '../../../lib/analytics/events/recommended'
import { useDispensaryContext } from '../../../lib/dispensaries'
import { productCardImageSourceProps } from '../../../lib/products/card'
import { ParentProductSelectionForModal } from '../../../lib/products/modal/types'
import { openQuickViewProductModal } from '../../../lib/products/quick-view-modal'
import { handleProductTagCategoryPriority } from '../../../lib/products/tags'
import { tryParsingSanityImageJson } from '../../../lib/sanity'
import { formatAsCurrency } from '../../../utils/priceTools'
import Link from '../../global/Link'

export type ParentProductCardGridProps = {
  className?: string
  sanityProductDetails?: ParentProductSelectionForModal
  analyticsEventAppContext?: AnalyticsEventAddToCart['app_context']
  product: Pick<
    ParentProduct,
    'name' | 'urlKey' | 'sku' | 'priceRange' | 'retailPriceRange' | 'variantAttributes'
  > & {
    mainImage?: Maybe<Pick<SanityImage, 'json'>>
    tagCategories?: string[]
  }
}

// TODO: Check if isAuthenticated is needed after M2 update to remove prices for guests
const ParentProductGridCard: FC<ParentProductCardGridProps> = ({
  product,
  analyticsEventAppContext,
  sanityProductDetails,
}) => {
  const dispensary = useDispensaryContext()
  const { shouldDisplayRetailPrice } = useRetailPrices()

  const sanityImageSource = product.mainImage?.json
    ? tryParsingSanityImageJson(product.mainImage.json)
    : undefined

  const productPrice =
    product.priceRange?.minimum &&
    formatAsCurrency({ amount: parseFloat(product.priceRange?.minimum) })
  const productRetailPrice =
    product.retailPriceRange?.minimum &&
    shouldDisplayRetailPrice &&
    formatAsCurrency({ amount: parseFloat(product.retailPriceRange?.minimum) })

  const hasPriceRange = product.priceRange?.maximum !== product.priceRange?.minimum

  const { shouldShowTestKitPrice } = useTestKitPrice()

  // NOTE: The labels are not implmented in the new design
  return (
    <ProductCard
      title={<Link to={`/products/${product.urlKey}/`}>{product.name}</Link>}
      attributes={product.variantAttributes}
      image={{
        ...productCardImageSourceProps(sanityImageSource),
        loading: 'lazy',
        alt: product.name || '',
      }}
      tag={handleProductTagCategoryPriority(product.tagCategories || [])}
      price={productPrice}
      retailPrice={productRetailPrice || undefined}
      onAddToCart={() =>
        openQuickViewProductModal({
          sku: product.sku,
          urlKey: product.urlKey,
          productDetails: sanityProductDetails,
          analyticsEventAppContext:
            analyticsEventAppContext || dispensary ? 'dispensary_plp' : 'plp',
        })
      }
      hasPriceRange={hasPriceRange}
      showPrice={shouldShowTestKitPrice(product.sku)}
    />
  )
}

export default ParentProductGridCard
